import { useEffect, type FC } from 'react';
import { snakeCase } from 'lodash-es';
import { AccordionItem } from '@carbon/react';
import type { ColumnFiltersState } from '@tanstack/react-table';
import { FilterSection } from 'components/data-table';
import { CheckboxGroupFilter } from 'components/data-table/filter/checkbox-group-filter';
import { MultiSelectFilter } from 'components/data-table/filter/multi-select-filter';
import { RailSectionTitle } from 'components/workspace-cards';
import type { CrmTractFacetItem } from 'types/api-responses';
import type { OwnershipComparisonRow, PagedFilters } from '../types';
import {
  MATCH_LABELS,
  REPORT_AGREEMENT_LABELS,
  VERDICT_LABELS,
  sourceFilenames,
  sourceSheetNames,
} from './columns';
import styles from './page.module.scss';

type Option = { value: string; label: string };

export type TableFilters = Record<string, string[]>;
export type FilterTable = 'tracts' | 'mor' | 'runsheet' | 'ownership';
export type WorkspaceFilters = Record<FilterTable, TableFilters>;
export interface TableRows {
  rows: readonly Record<string, unknown>[];
  facets?: Record<string, CrmTractFacetItem[]>;
}
export type ReportRows = (table: FilterTable, rows: TableRows) => void;

export const EMPTY_FILTERS: WorkspaceFilters = {
  tracts: {},
  mor: {},
  runsheet: {},
  ownership: {},
};

const fromLabels = (labels: Record<string, string>): Option[] =>
  Object.entries(labels).map(([value, label]) => ({ value, label }));

export const OWNERSHIP_FLAGS: Record<string, string> = {
  crm_inconsistent: 'CRM inconsistent',
  reports_disagree: 'Reports disagree',
  not_in_workspace: 'Not in workspace',
};

export const ownershipFlags = (row: OwnershipComparisonRow): string[] =>
  [
    row.crmInconsistent && 'crm_inconsistent',
    row.reportsDisagree && 'reports_disagree',
    !row.tractInWorkspace && 'not_in_workspace',
  ].filter((flag): flag is string => Boolean(flag));

export const ownershipTract = (row: OwnershipComparisonRow) =>
  row.tractName ?? row.salesforceTractId;

const applied = (filters: TableFilters) =>
  Object.entries(filters).filter(([, values]) => values.length > 0);

export const toColumnFilters = (filters: TableFilters): ColumnFiltersState =>
  applied(filters).map(([id, value]) => ({ id, value }));

// The client-side twin of toColumnFilters, for rows no DataTable filters.
export const matchesFilters = (
  row: Record<string, unknown>,
  filters: TableFilters
): boolean =>
  applied(filters).every(([id, values]) => values.includes(String(row[id])));

// The server filters each column by its snake_case name.
export const toParams = (filters: TableFilters): PagedFilters =>
  Object.fromEntries(
    applied(filters).map(([id, values]) => [snakeCase(id), values])
  );

// Rows must be stable between renders, or this reports in a loop.
export const useReportRows = (
  table: FilterTable,
  report: ReportRows,
  rows: TableRows['rows'],
  facets?: TableRows['facets']
) =>
  useEffect(
    () => report(table, { rows, facets }),
    [table, report, rows, facets]
  );

interface Field {
  id: string;
  label: string;
  /** Fixed choices render as checkboxes; row-derived ones as a searchable list. */
  choices?: Option[];
  /** A row's values for this filter; its `id` field by default. */
  values?: (row: never) => unknown;
}

const NOT_IN_WORKSPACE: Field = {
  id: 'tractInWorkspace',
  label: 'Workspace',
  choices: [{ value: 'false', label: 'Not in workspace' }],
};

const TABLES: { table: FilterTable; title: string; fields: Field[] }[] = [
  {
    table: 'tracts',
    title: 'Tracts Filters',
    fields: [
      { id: 'entityName', label: 'Insights Entity' },
      { id: 'accountName', label: 'CRM Account' },
      { id: 'instrumentType', label: 'Instrument Type' },
      { id: 'countyName', label: 'County' },
    ],
  },
  {
    table: 'mor',
    title: 'Ownership Filters',
    fields: [
      { id: 'tractIdentifier', label: 'Tract' },
      { id: 'entityName', label: 'Entity' },
      {
        id: 'reportAgreement',
        label: 'Report Agreement',
        choices: fromLabels(REPORT_AGREEMENT_LABELS),
      },
      NOT_IN_WORKSPACE,
    ],
  },
  {
    table: 'runsheet',
    title: 'Runsheet Comparison Filters',
    fields: [
      {
        id: 'matchStatus',
        label: 'Match Status',
        choices: fromLabels(MATCH_LABELS),
      },
      { id: 'documentType', label: 'Document Type' },
      { id: 'countyName', label: 'County' },
      NOT_IN_WORKSPACE,
      { id: 'workbook', label: 'Workbook', values: sourceFilenames },
      { id: 'sheetName', label: 'Sheet', values: sourceSheetNames },
    ],
  },
  {
    table: 'ownership',
    title: 'Ownership Comparison Filters',
    fields: [
      { id: 'verdict', label: 'Verdict', choices: fromLabels(VERDICT_LABELS) },
      { id: 'flags', label: 'Flags', choices: fromLabels(OWNERSHIP_FLAGS) },
      { id: 'stick', label: 'Stick' },
      { id: 'tract', label: 'Tract', values: ownershipTract },
      { id: 'entityName', label: 'Entity' },
      { id: 'workbook', label: 'Workbook', values: sourceFilenames },
      { id: 'sheetName', label: 'Sheet', values: sourceSheetNames },
    ],
  },
];

const optionsOf = (field: Field, table?: TableRows): Option[] => {
  const facet = table?.facets?.[field.id];
  if (facet)
    return facet.map((item) => ({ value: item.id, label: item.value }));
  const values = (field.values ?? ((row) => row[field.id])) as (
    row: Record<string, unknown>
  ) => unknown;
  const present = (table?.rows ?? [])
    .flatMap(values)
    .filter((value): value is string => typeof value === 'string' && !!value);
  return [...new Set(present)].sort().map((value) => ({ value, label: value }));
};

export const RailFilters: FC<{
  filters: WorkspaceFilters;
  rows: Partial<Record<FilterTable, TableRows>>;
  onChange: (table: FilterTable, next: TableFilters) => void;
}> = ({ filters, rows, onChange }) => (
  <>
    {TABLES.map(({ table, title, fields }) => (
      <AccordionItem
        key={table}
        title={
          <RailSectionTitle
            label={title}
            count={applied(filters[table]).length}
          />
        }
      >
        <div className={styles.filters}>
          {fields.map((field) => {
            const id = `tsw-${table}-${field.id}`;
            const value = filters[table][field.id] ?? [];
            const set = (next: string[]) =>
              onChange(table, { ...filters[table], [field.id]: next });
            if (field.choices) {
              return (
                <FilterSection key={field.id} title={field.label}>
                  <CheckboxGroupFilter
                    id={id}
                    value={value}
                    onChange={set}
                    options={field.choices}
                  />
                </FilterSection>
              );
            }
            return (
              <MultiSelectFilter
                key={field.id}
                id={id}
                label={field.label}
                value={value}
                onChange={set}
                options={optionsOf(field, rows[table])}
              />
            );
          })}
        </div>
      </AccordionItem>
    ))}
  </>
);
